// src/pages/Posts/CreatePostModal.jsx

import React from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faTimes, faEye } from '@fortawesome/free-solid-svg-icons';
import { useCreatePost } from '../../hooks/useCreatePost';

import PostInformationForm from './components/CreatePostModalComponents/PostInformationForm';
import PostFileUpload from './components/CreatePostModalComponents/PostFileUpload';
import ContentOptionsForm from '../../components/content/ContentOptionsForm';
import PremiumContentOptionsForm from '../../components/content/PremiumContentOptionsForm';
import FormToggle from '../../components/common/FormToggle';
import CoverImagePreview from '../../components/common/CoverImagePreview';
import EffectSelectorModal from '../Packs/components/PackEditMediaComponents/EffectSelectorModal';

const CreatePostModal = ({ onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [isUserView, setIsUserView] = React.useState(false);

  const {
    postData,
    setPostData,
    mainFile,
    mainThumbnailFile,
    mainFileEffect,
    coverFile,
    coverFileEffect,
    handleFileSelect,
    handleCoverFileChange,
    handleChange,
    handleSubmit,
    effectsData,
    loadingEffects,
    isEffectModalOpen,
    setIsEffectModalOpen,
    handleOpenEffectModal,
    handleModalEffectSelect,
    handleModalIntensityChange,
    handleConfirmEffect,
    getDisplayUrlForModal,
    modalPreviewState,
    isPreviewing,
    isPending,
    uploadProgress,
    error,
    temporaryCoverPreviewUrl,
  } = useCreatePost(onSuccess);

  return (
    <>
      <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/70 backdrop-blur-sm p-4 animate-fadeIn">
        <div
          className="max-w-lg w-full bg-white dark:bg-neutral-900 rounded-xl shadow-card flex flex-col max-h-[95vh]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center p-4 border-b border-neutral-200 dark:border-neutral-700">
            <h2 className="text-xl font-bold font-display text-neutral-900 dark:text-white">
              {t('posts.create_modal.title')}
            </h2>
            <button
              onClick={onClose}
              disabled={isPending}
              type="button"
              className="w-8 h-8 flex items-center justify-center rounded-full text-neutral-600 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          <form
            onSubmit={handleSubmit}
            className="flex flex-col overflow-hidden h-full"
          >
            <div className="flex flex-col gap-6 p-6 overflow-y-auto">
              {error && (
                <div className="p-3 rounded-lg text-red-700 bg-red-50 dark:bg-red-500/10 dark:text-red-300 border text-sm">
                  {error}
                </div>
              )}
              <PostInformationForm
                title={postData.title}
                content={postData.content}
                onChange={handleChange}
                disabled={isPending}
              />
              {postData.isPremium && (
                <div className="p-3 rounded-lg bg-primary/5 dark:bg-primary/10 border border-primary/20 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon={faEye} className="text-primary" />
                    <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">
                      {t('posts.create_modal.user_preview_toggle_label')}
                    </span>
                  </div>
                  <FormToggle
                    name="user_view"
                    checked={isUserView}
                    onChange={() => setIsUserView((prev) => !prev)}
                  />
                </div>
              )}
              <PostFileUpload
                file={mainFile}
                thumbnailFile={mainThumbnailFile}
                onFileSelect={handleFileSelect}
                isPremium={postData.isPremium}
                onEditEffect={() => handleOpenEffectModal('main')}
                fileEffect={mainFileEffect}
                label={t('createPostModal.mainFile.label')}
                description={t('createPostModal.mainFile.description')}
                isUserView={isUserView}
              />
              <CoverImagePreview
                onFileSelect={handleCoverFileChange}
                stagedFile={coverFile}
                onFileRemove={() => handleCoverFileChange(null)}
                label={t('createPostModal.coverFile.label')}
                description={t('createPostModal.coverFile.description')}
                isPremium={postData.isPremium}
                disabled={isPending}
                onEditEffect={() => handleOpenEffectModal('cover')}
                coverFileEffect={coverFileEffect}
                isUserView={isUserView}
                // --- CORRECCIÓN FINAL ---
                // La prop que espera el componente se llama `temporaryPreviewUrl` (sin "Cover").
                temporaryPreviewUrl={temporaryCoverPreviewUrl}
              />
              <div className="space-y-4 p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
                <ContentOptionsForm
                  isPublic={postData.isPublic}
                  isPremium={postData.isPremium}
                  onChange={handleChange}
                  disabled={isPending}
                />
                <hr className="border-neutral-200 dark:border-neutral-700" />
                <FormToggle
                  label={t('posts.comments.enable_comments_label')}
                  name="commentsEnabled"
                  checked={postData.commentsEnabled}
                  onChange={handleChange}
                  disabled={isPending}
                />
              </div>
              {postData.isPremium && (
                <div className="p-4 rounded-lg bg-neutral-50 dark:bg-neutral-800/50 border border-neutral-200 dark:border-neutral-700">
                  <PremiumContentOptionsForm
                    price={postData.price}
                    onPriceChange={(price) =>
                      setPostData((prev) => ({ ...prev, price }))
                    }
                    disabled={isPending}
                    loadingEffects={loadingEffects}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 p-4 mt-auto border-t border-neutral-200 dark:border-neutral-700">
              <button
                type="button"
                className="btn-secondary"
                onClick={onClose}
                disabled={isPending}
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                className="btn-primary min-w-[120px]"
                disabled={
                  isPending || (uploadProgress !== null && uploadProgress < 100)
                }
              >
                {isPending ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                    {uploadProgress !== null
                      ? `${t('common.uploading')} ${uploadProgress}%`
                      : t('common.publishing')}
                  </>
                ) : (
                  t('common.publish')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
      {isEffectModalOpen && (
        <EffectSelectorModal
          isOpen={isEffectModalOpen}
          onClose={() => setIsEffectModalOpen(false)}
          displayImage={getDisplayUrlForModal()}
          availableEffects={effectsData?.effects || {}}
          onSelectEffect={handleModalEffectSelect}
          onConfirm={handleConfirmEffect}
          isPreviewing={isPreviewing}
          previewState={modalPreviewState}
          onIntensityChange={handleModalIntensityChange}
        />
      )}
    </>
  );
};

export default CreatePostModal;
